import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { TrendingUp, TrendingDown, Remove } from '@mui/icons-material';
import { motion } from 'framer-motion';
import spacing from '../../design-system/spacing';
import shadows from '../../design-system/shadows';

/**
 * StatCard - Card de estadística con icono, valor, cambio y sparkline opcional
 * Componente moderno para mostrar KPIs y métricas importantes
 */

const StyledCard = styled(motion.div)(({ variant = 'default', theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: spacing.borderRadius.lg,
  padding: spacing[4],
  boxShadow: shadows.card,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  
  '&:hover': {
    boxShadow: shadows.cardHover,
    transform: 'translateY(-2px)',
    borderColor: theme.palette.primary.light,
  },

  // Variante con color de fondo
  ...(variant === 'colored' && {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${theme.palette.background.paper} 100%)`,
    borderColor: theme.palette.primary.light,
  }),

  ...(variant === 'success' && {
    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.12)} 0%, ${theme.palette.background.paper} 100%)`,
    borderColor: theme.palette.success.main,
  }),

  ...(variant === 'warning' && {
    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.12)} 0%, ${theme.palette.background.paper} 100%)`,
    borderColor: theme.palette.warning.main,
  }),

  ...(variant === 'error' && {
    background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.12)} 0%, ${theme.palette.background.paper} 100%)`,
    borderColor: theme.palette.error.main,
  }),
}));

const IconContainer = styled(Box)(({ iconColor, theme }) => ({
  width: 48,
  height: 48,
  borderRadius: spacing.borderRadius.md,
  backgroundColor: iconColor || theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: spacing[2],
  
  '& svg': {
    color: theme.palette.primary.contrastText,
    fontSize: 24,
  },
}));

const getTrendColor = (trend, theme) => {
  if (!trend || trend === 0) return theme.palette.text.secondary;
  return trend > 0 ? theme.palette.success.main : theme.palette.error.main;
};

const getTrendIcon = (trend) => {
  if (!trend || trend === 0) return <Remove fontSize="small" />;
  return trend > 0 ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />;
};

const StatCard = ({
  title,
  value,
  icon: Icon,
  iconColor,
  trend, // Número: +5, -3, 0 o null
  trendLabel, // "vs mes anterior", "esta semana", etc.
  suffix = '', // "%", "pts", "goles", etc.
  variant = 'default',
  onClick,
  sparklineData, // Array de números para mini gráfico
  loading = false,
}) => {
  const theme = useTheme();

  return (
    <StyledCard
      variant={variant}
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Icono */}
      {Icon && (
        <IconContainer iconColor={iconColor}>
          <Icon />
        </IconContainer>
      )}

      {/* Título */}
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          display: 'block',
          mb: 0.5,
        }}
      >
        {title}
      </Typography>

      {/* Valor */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography
          variant="h3"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 700,
            fontSize: { xs: 28, sm: 32 },
          }}
        >
          {loading ? '...' : value}
        </Typography>
        {suffix && (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
          >
            {suffix}
          </Typography>
        )}
      </Box>

      {/* Tendencia */}
      {(trend !== undefined && trend !== null) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: getTrendColor(trend, theme),
            }}
          >
            {getTrendIcon(trend)}
            <Typography variant="body2" fontWeight={600} sx={{ ml: 0.5 }}>
              {trend > 0 ? '+' : ''}{trend}%
            </Typography>
          </Box>
          {trendLabel && (
            <Typography variant="caption" color={theme.palette.text.secondary}>
              {trendLabel}
            </Typography>
          )}
        </Box>
      )}

      {/* Sparkline (mini gráfico - implementación simple) */}
      {sparklineData && sparklineData.length > 0 && (
        <Box
          sx={{
            mt: 2,
            height: 40,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0.5,
          }}
        >
          {sparklineData.slice(-10).map((value, index) => {
            const max = Math.max(...sparklineData);
            const height = (value / max) * 100;
            return (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: `${height}%`,
                  backgroundColor: theme.palette.primary.light,
                  borderRadius: spacing.borderRadius.sm,
                  opacity: 0.6 + (index / sparklineData.length) * 0.4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              />
            );
          })}
        </Box>
      )}
    </StyledCard>
  );
};

export default StatCard;
